/**
 * /admin/categories — Super-admin category management (Phase W-3).
 *
 * Master taxonomy CRUD that powers customer filter chips + owner
 * brand-settings picker. Mirrors the OwnersPage scaffold: page header
 * + table + Modal-based create/edit + soft-delete confirm.
 *
 * Icon picker is intentionally lo-fi (text input + collapsible grid of
 * curated common Lucide names) to avoid a heavy dependency. Names that
 * don't resolve at render time fall back to the generic Tag icon.
 */
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, Tags, Search, Tag as TagIcon, Coffee, IceCream,
  UtensilsCrossed, Shirt, Sparkles, Flower2, BedDouble, Dumbbell,
  ShoppingBag, Music, HeartPulse, GraduationCap, Car, Plane, Briefcase,
  MoreHorizontal, Pizza, Wine, Cake, Camera, Watch, Scissors, Paintbrush,
  Hammer, Wrench, Heart, Star, MapPin, Croissant, RotateCcw, AlertTriangle,
} from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import {
  PageHeader, SectionCard, FullSpinner, QueryError, EmptyState, StatusPill,
} from '@/components/ui_primitives/Misc';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui_primitives/Table';
import Button from '@/components/ui_primitives/Button';
import Modal from '@/components/ui_primitives/Modal';
import { FieldRow, Input } from '@/components/ui_primitives/Field';

// Curated icon catalog. Render via `ICONS[name] ?? TagIcon` so unknown
// names degrade gracefully instead of erroring.
const ICONS = {
  Coffee, IceCream, UtensilsCrossed, Shirt, Sparkles, Flower2, BedDouble,
  Dumbbell, ShoppingBag, Music, HeartPulse, GraduationCap, Car, Plane,
  Briefcase, MoreHorizontal, Pizza, Wine, Cake, Camera, Watch, Scissors,
  Paintbrush, Hammer, Wrench, Heart, Star, MapPin, Croissant, Tag: TagIcon,
};
const ICON_NAMES = Object.keys(ICONS).filter((k) => k !== 'Tag');

function renderIcon(name, props = {}) {
  const C = ICONS[name] ?? TagIcon;
  return <C {...props} />;
}

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 60);

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

const schema = z.object({
  name: z.string().min(1).max(60),
  slug: z.string().min(1).max(60).regex(/^[a-z0-9-]+$/, 'lowercase letters, digits, hyphens only'),
  icon: z.string().max(40).optional().or(z.literal('')),
  color: z.string().regex(HEX_RE, 'Hex like #D4AF37').optional().or(z.literal('')),
  sort_index: z.coerce.number().int().min(0).max(999),
  is_active: z.boolean().default(true),
});

export default function AdminCategoriesPage() {
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();

  const [search, setSearch] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [editing, setEditing] = useState(null);      // null | 'new' | row
  const [deleteTarget, setDeleteTarget] = useState(null);

  const queryOpts = useMemo(
    () => ({ include_inactive: includeInactive, include_deleted: includeDeleted }),
    [includeInactive, includeDeleted],
  );

  const categoriesQ = useQuery({
    queryKey: qk.adminCategories(queryOpts),
    queryFn: () => api.adminCategories(token, queryOpts),
    enabled: !!token,
  });
  // Brand list — used for the `# brands referencing` column.
  const brandsQ = useQuery({
    queryKey: qk.adminBrands(),
    queryFn: () => api.adminBrands(token),
    enabled: !!token,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin-categories'] });
    qc.invalidateQueries({ queryKey: qk.categories() });
  };

  const deleteMut = useMutation({
    mutationFn: (id) => api.adminDeleteCategory(token, id),
    onSuccess: () => { toast.success('Category deleted'); invalidate(); setDeleteTarget(null); },
    onError: (e) => toast.error(String(e?.detail?.message ?? e?.detail ?? 'Delete failed')),
  });

  if (categoriesQ.isLoading) return <FullSpinner />;
  if (categoriesQ.error) return <QueryError error={categoriesQ.error} onRetry={() => categoriesQ.refetch()} />;

  const items = (categoriesQ.data || []).slice().sort(
    (a, b) => (a.sort_index - b.sort_index) || a.name.localeCompare(b.name),
  );
  // Brand → category usage map (only counts non-deleted brands).
  const usage = new Map();
  for (const b of brandsQ.data || []) {
    if (b.deleted_at) continue;
    for (const cid of (b.category_ids ?? [])) {
      usage.set(cid, (usage.get(cid) ?? 0) + 1);
    }
  }
  const filtered = items.filter((c) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.name.toLowerCase().includes(s) || c.slug.toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="admin-categories-screen">
      <PageHeader
        kicker="Taxonomy"
        title="Categories"
        subtitle="Manage the master taxonomy that brands and customers see across OneStamp."
        actions={
          <Button
            leftIcon={<Plus size={14} />}
            onClick={() => setEditing('new')}
            testID="admin-category-create"
          >
            New category
          </Button>
        }
      />

      {/* Filters bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or slug…"
            data-testid="admin-category-search"
            className="w-full bg-[#1A1A1A] border border-white/10 rounded-xl pl-9 pr-3 py-2 text-sm placeholder:text-white/30 focus:outline-none focus:border-gold/40"
          />
        </div>
        <TogglePill
          label="Include inactive"
          on={includeInactive}
          onChange={setIncludeInactive}
          testID="admin-category-toggle-inactive"
        />
        <TogglePill
          label="Include deleted"
          on={includeDeleted}
          onChange={setIncludeDeleted}
          testID="admin-category-toggle-deleted"
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon={<Tags size={20} />}
          title={search ? 'No matches' : 'No categories yet'}
          subtitle={search ? 'Try a different search.' : 'Seed the W-3 taxonomy or add one manually.'}
        />
      ) : (
        <SectionCard padded={false}>
          <Table testID="admin-categories-table">
            <THead>
              <Tr>
                <Th>#</Th>
                <Th>Category</Th>
                <Th>Slug</Th>
                <Th>Brands</Th>
                <Th>Status</Th>
                <Th align="right">Actions</Th>
              </Tr>
            </THead>
            <TBody>
              {filtered.map((c) => {
                const usageN = usage.get(c.id) ?? 0;
                const isDeleted = !!c.deleted_at;
                return (
                  <Tr key={c.id} data-testid={`admin-category-row-${c.id}`}>
                    <Td>
                      <span className="font-mono text-xs text-white/55">
                        {c.sort_index}
                      </span>
                    </Td>
                    <Td>
                      <div className="flex items-center gap-3">
                        <CategoryChipPreview cat={c} compact />
                      </div>
                    </Td>
                    <Td>
                      <span className="font-mono text-xs text-white/70">{c.slug}</span>
                    </Td>
                    <Td>
                      <span
                        className={`text-sm font-medium ${
                          usageN > 0 ? 'text-white' : 'text-white/35'
                        }`}
                        data-testid={`admin-category-usage-${c.id}`}
                      >
                        {usageN}
                      </span>
                    </Td>
                    <Td>
                      {isDeleted ? (
                        <StatusPill variant="slate">Deleted</StatusPill>
                      ) : c.is_active ? (
                        <StatusPill variant="gold">Active</StatusPill>
                      ) : (
                        <StatusPill variant="slate">Inactive</StatusPill>
                      )}
                    </Td>
                    <Td align="right">
                      <div className="inline-flex gap-1.5">
                        <Button
                          size="sm"
                          variant="subtle"
                          leftIcon={<Edit size={12} />}
                          onClick={() => setEditing(c)}
                          disabled={isDeleted}
                          testID={`admin-category-edit-${c.id}`}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          leftIcon={<Trash2 size={12} />}
                          onClick={() => setDeleteTarget({ cat: c, usage: usageN })}
                          disabled={isDeleted}
                          testID={`admin-category-delete-${c.id}`}
                        >
                          Delete
                        </Button>
                      </div>
                    </Td>
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        </SectionCard>
      )}

      {editing ? (
        <CategoryFormModal
          category={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { invalidate(); setEditing(null); }}
        />
      ) : null}

      {deleteTarget ? (
        <DeleteCategoryModal
          cat={deleteTarget.cat}
          usage={deleteTarget.usage}
          busy={deleteMut.isPending}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => deleteMut.mutate(deleteTarget.cat.id)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared chip preview — also reused by BrandsPage for the per-row
// category cell. Exported so consumers can render the same visual
// language without duplicating the Tailwind soup.
// ---------------------------------------------------------------------------
export function CategoryChipPreview({ cat, compact = false }) {
  const color = cat.color || '#9CA3AF';
  // 18% alpha background + 38% border — keeps the chip readable on the
  // dark surface without overpowering the gold accent palette.
  return (
    <div className="flex items-center gap-2">
      <span
        className={`inline-flex items-center gap-1.5 rounded-full ${
          compact ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-1 text-xs'
        } font-medium`}
        style={{
          color,
          backgroundColor: `${color}22`,
          border: `1px solid ${color}55`,
        }}
      >
        {renderIcon(cat.icon, { size: compact ? 11 : 12 })}
        {cat.name}
      </span>
      {!compact ? (
        <span className="font-mono text-[10px] text-white/35">{cat.slug}</span>
      ) : null}
    </div>
  );
}

function TogglePill({ label, on, onChange, testID }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      data-testid={testID}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
        on
          ? 'bg-gold/15 border-gold/40 text-gold'
          : 'bg-[#1A1A1A] border-white/10 text-white/65 hover:border-white/20'
      }`}
    >
      {label}
    </button>
  );
}

function CategoryFormModal({ category, onClose, onSaved }) {
  const token = useAuthStore((s) => s.token);
  const isEdit = !!category;
  const [showIconGrid, setShowIconGrid] = useState(false);
  const [formError, setFormError] = useState(null);

  const { register, handleSubmit, setValue, watch, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: category?.name ?? '',
      slug: category?.slug ?? '',
      icon: category?.icon ?? '',
      color: category?.color ?? '#D4AF37',
      sort_index: category?.sort_index ?? 0,
      is_active: category?.is_active ?? true,
    },
  });

  const liveName = watch('name');
  const liveIcon = watch('icon');
  const liveColor = watch('color');

  // Slug auto-derive: in CREATE flow, slugify from name unless user
  // has manually typed a slug already. In EDIT flow, don't auto-touch
  // (would surprise the user).
  const onNameChange = (e) => {
    const v = e.target.value;
    setValue('name', v);
    if (!isEdit) {
      setValue('slug', slugify(v), { shouldValidate: true });
    }
  };

  const onSubmit = async (vals) => {
    setFormError(null);
    const payload = {
      name: vals.name,
      slug: vals.slug,
      icon: vals.icon || null,
      color: vals.color || null,
      sort_index: Number(vals.sort_index),
      is_active: !!vals.is_active,
    };
    try {
      if (isEdit) {
        await api.adminUpdateCategory(token, category.id, payload);
        toast.success('Category updated');
      } else {
        await api.adminCreateCategory(token, payload);
        toast.success('Category created');
      }
      onSaved();
    } catch (e) {
      const code = e?.detail?.code;
      if (code === 'slug_in_use') {
        setFormError({ field: 'slug', message: 'Slug already in use' });
      } else {
        toast.error(String(e?.detail?.message ?? e?.detail ?? e?.message ?? 'Save failed'));
      }
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? `Edit "${category.name}"` : 'New category'}
      size="md"
      subtitle="Customers see this as a filter chip on the All Brands page."
      testID="admin-category-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            loading={isSubmitting}
            testID="admin-category-submit"
          >
            {isEdit ? 'Save changes' : 'Create'}
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <FieldRow label="Name" required error={errors.name?.message}>
          <Input
            {...register('name')}
            onChange={onNameChange}
            data-testid="admin-category-name-input"
          />
        </FieldRow>
        <FieldRow
          label="Slug"
          required
          error={formError?.field === 'slug' ? formError.message : errors.slug?.message}
          hint="URL-safe handle. Lowercase, digits, hyphens only."
        >
          <Input
            {...register('slug')}
            data-testid="admin-category-slug-input"
            className="font-mono text-sm"
          />
        </FieldRow>

        <FieldRow label="Icon (Lucide name)" hint="Free text — click 'Browse common icons' for a quick gallery.">
          <div className="space-y-2">
            <Input
              {...register('icon')}
              placeholder="Coffee"
              data-testid="admin-category-icon-input"
            />
            <button
              type="button"
              onClick={() => setShowIconGrid((v) => !v)}
              data-testid="admin-category-icon-toggle"
              className="text-xs text-gold hover:text-gold/80 transition-colors"
            >
              {showIconGrid ? 'Hide gallery ↑' : 'Browse common icons ↓'}
            </button>
            {showIconGrid ? (
              <div className="grid grid-cols-6 gap-2 p-3 rounded-xl bg-[#1A1A1A] border border-white/8">
                {ICON_NAMES.map((n) => {
                  const active = n === liveIcon;
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setValue('icon', n, { shouldValidate: true })}
                      title={n}
                      className={`w-10 h-10 grid place-items-center rounded-lg border transition-colors ${
                        active
                          ? 'border-gold bg-gold/15 text-gold'
                          : 'border-white/8 bg-white/[0.03] text-white/65 hover:border-white/20 hover:text-white'
                      }`}
                    >
                      {renderIcon(n, { size: 14 })}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </FieldRow>

        <FieldRow label="Color" hint="Hex tint used for the customer chip background.">
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={liveColor && HEX_RE.test(liveColor) ? liveColor : '#D4AF37'}
              onChange={(e) => setValue('color', e.target.value, { shouldValidate: true })}
              className="w-10 h-10 rounded-lg cursor-pointer border border-white/10 bg-transparent p-0"
              data-testid="admin-category-color-picker"
            />
            <Input
              {...register('color')}
              placeholder="#D4AF37"
              className="font-mono"
              data-testid="admin-category-color-input"
            />
          </div>
          {errors.color?.message ? (
            <div className="text-xs text-rose-400 mt-1">{errors.color.message}</div>
          ) : null}
        </FieldRow>

        <FieldRow
          label="Sort index"
          hint="Lower numbers appear first in the filter chip row. 0–999."
        >
          <Input
            type="number"
            min={0}
            max={999}
            {...register('sort_index')}
            data-testid="admin-category-sort-input"
          />
        </FieldRow>

        <FieldRow label="Status" hint="Inactive categories vanish from the customer chip row.">
          <label className="inline-flex items-center gap-2 text-sm text-white/85">
            <input
              type="checkbox"
              className="accent-gold w-4 h-4"
              {...register('is_active')}
              data-testid="admin-category-active-toggle"
            />
            Active
          </label>
        </FieldRow>

        {/* Live preview */}
        <div
          className="rounded-xl bg-[#0F0F14] border border-white/8 p-4 mt-4"
          data-testid="admin-category-preview"
        >
          <div className="text-[10px] uppercase tracking-wider font-mono text-gold/85 mb-2">
            Customer chip preview
          </div>
          <CategoryChipPreview
            cat={{
              name: liveName || 'Category name',
              icon: liveIcon || 'Tag',
              color: HEX_RE.test(liveColor) ? liveColor : '#D4AF37',
              slug: '',
            }}
          />
        </div>
      </form>
    </Modal>
  );
}

function DeleteCategoryModal({ cat, usage, busy, onClose, onConfirm }) {
  return (
    <Modal
      open
      onClose={onClose}
      title={`Delete "${cat.name}"?`}
      size="md"
      testID="admin-category-delete-modal"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            loading={busy}
            leftIcon={<Trash2 size={12} />}
            testID="admin-category-delete-confirm"
          >
            Delete
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-white/75" data-testid="admin-category-delete-modal-body">
        {usage > 0 ? (
          <div
            className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-amber-500/10 border border-amber-400/35 text-amber-200"
            data-testid="admin-category-delete-usage-warning"
          >
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <div className="text-xs leading-relaxed">
              <strong>{usage}</strong> brand{usage === 1 ? '' : 's'} still
              reference this category. They will automatically fall back to{' '}
              <strong>'Other'</strong> until reassigned.
            </div>
          </div>
        ) : (
          <div
            className="flex items-start gap-2 px-3 py-2.5 rounded-xl bg-emerald-500/10 border border-emerald-400/30 text-emerald-200"
            data-testid="admin-category-delete-usage-ok"
          >
            <RotateCcw size={14} className="shrink-0 mt-0.5" />
            <div className="text-xs leading-relaxed">
              No brands currently reference this category. It can be safely
              soft-deleted.
            </div>
          </div>
        )}
        <p className="text-[11px] text-white/40 flex items-center gap-1.5">
          <RotateCcw size={10} /> Soft delete — the row stays in the database
          and can be restored by engineering if needed.
        </p>
      </div>
    </Modal>
  );
}
